import * as m from '$lib/paraglide/messages.js';
import { baseLocale, locales, localizeUrl, type Locale } from '$lib/paraglide/runtime';

export const ZARPAR_ORIGIN = 'https://zarpar.eu';

export const MARKETING_DEFAULT_OG_IMAGE = '/img/landing/redesign-2025/photos/calm-misty.avif';
export const MARKETING_GIFT_OG_IMAGE = '/img/gifts/Zarpar-regalo-500.webp';
export const ZARPAR_LOGO_URL = `${ZARPAR_ORIGIN}/img/logo/logo-big.png`;

export type MarketingSeoMeta = {
	title: string;
	description: string;
	/** Path relative to site root for og:image */
	image?: string;
	/** LCP hero image to preload (home / PageHero) */
	preloadImage?: string;
};

const OG_LOCALE: Record<Locale, string> = {
	es: 'es_ES',
	en: 'en_US',
	gl: 'gl_ES'
};

export function absoluteUrl(path: string): string {
	if (path.startsWith('http://') || path.startsWith('https://')) {
		return path;
	}
	return `${ZARPAR_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

export function localizedAbsolutePath(canonicalPath: string, locale: Locale): string {
	const url = localizeUrl(new URL(canonicalPath, ZARPAR_ORIGIN), { locale });
	return url.pathname;
}

export function localizedAbsoluteUrl(canonicalPath: string, locale: Locale): string {
	return absoluteUrl(localizedAbsolutePath(canonicalPath, locale));
}

export function marketingHreflangLinks(canonicalPath: string): { lang: string; href: string }[] {
	const links: { lang: string; href: string }[] = locales.map((locale) => ({
		lang: locale,
		href: localizedAbsoluteUrl(canonicalPath, locale)
	}));
	links.push({
		lang: 'x-default',
		href: localizedAbsoluteUrl(canonicalPath, baseLocale)
	});
	return links;
}

export function ogLocale(locale: Locale): string {
	return OG_LOCALE[locale] ?? OG_LOCALE.es;
}

export function ogLocaleAlternates(currentLocale: Locale): string[] {
	return locales.filter((l) => l !== currentLocale).map((l) => OG_LOCALE[l]);
}

/** De-localized marketing route → title, description, optional OG/LCP images. */
export function marketingSeoMeta(canonicalPath: string): MarketingSeoMeta | null {
	switch (canonicalPath) {
		case '/':
			return {
				title: m.landing_meta_title(),
				description: m.landing_meta_description()
			};
		case '/alquiler-lista-septima':
			return {
				title: m.l7_meta_title(),
				description: m.l7_meta_description()
			};
		case '/alquiler-lista-sexta':
			return {
				title: m.l6_meta_title(),
				description: m.l6_meta_description()
			};
		case '/zarpar-experiencias':
			return {
				title: m.exp_mk_meta_title(),
				description: m.exp_mk_meta_description()
			};
		case '/registro-capitan':
			return {
				title: m.cap_meta_title(),
				description: m.cap_meta_description()
			};
		case '/anadir-barco':
			return {
				title: m.ab_meta_title(),
				description: m.ab_meta_description()
			};
		case '/regala-zarpar':
			return {
				title: m.gift_meta_title(),
				description: m.gift_meta_description(),
				image: MARKETING_GIFT_OG_IMAGE
			};
		case '/barcos':
			return {
				title: m.barcos_meta_title(),
				description: m.barcos_meta_description()
			};
		default:
			return null;
	}
}

export type FaqSchemaItem = {
	question: string;
	answer: string;
};

/** Plain-text question label for FAQPage schema (no leading emojis). */
export function faqSchemaQuestionName(question: string): string {
	return question.replace(/^[\p{Extended_Pictographic}\uFE0F\s]+/gu, '').trim();
}

/** Plain-text answer for FAQPage schema (no markdown). */
export function faqSchemaAnswerText(answer: string): string {
	return answer
		.replace(/\*\*(.+?)\*\*/g, '$1')
		.replace(/\*(.+?)\*/g, '$1')
		.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
		.replace(/^>\s+/gm, '')
		.replace(/\n+/g, ' ')
		.trim();
}

export function faqStructuredData(input: {
	title: string;
	description: string;
	pageUrl: string;
	items: FaqSchemaItem[];
}): Record<string, unknown> {
	const webpage = {
		'@type': 'WebPage',
		'@id': `${input.pageUrl}#webpage`,
		url: input.pageUrl,
		name: input.title,
		description: input.description,
		isPartOf: { '@id': `${ZARPAR_ORIGIN}/#website` },
		about: { '@id': `${ZARPAR_ORIGIN}/#organization` }
	};

	return {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'Organization',
				'@id': `${ZARPAR_ORIGIN}/#organization`,
				name: 'Zarpar',
				url: ZARPAR_ORIGIN,
				logo: ZARPAR_LOGO_URL
			},
			{
				'@type': 'WebSite',
				'@id': `${ZARPAR_ORIGIN}/#website`,
				url: ZARPAR_ORIGIN,
				name: 'Zarpar',
				publisher: { '@id': `${ZARPAR_ORIGIN}/#organization` }
			},
			webpage,
			{
				'@type': 'FAQPage',
				'@id': `${input.pageUrl}#faq`,
				url: input.pageUrl,
				name: input.title,
				description: input.description,
				isPartOf: { '@id': `${input.pageUrl}#webpage` },
				mainEntity: input.items.map((item) => ({
					'@type': 'Question',
					name: faqSchemaQuestionName(item.question),
					acceptedAnswer: {
						'@type': 'Answer',
						text: faqSchemaAnswerText(item.answer)
					}
				}))
			}
		]
	};
}

export function marketingStructuredData(input: {
	title: string;
	description: string;
	pageUrl: string;
}): Record<string, unknown> {
	return {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'Organization',
				'@id': `${ZARPAR_ORIGIN}/#organization`,
				name: 'Zarpar',
				url: ZARPAR_ORIGIN,
				logo: ZARPAR_LOGO_URL
			},
			{
				'@type': 'WebSite',
				'@id': `${ZARPAR_ORIGIN}/#website`,
				url: ZARPAR_ORIGIN,
				name: 'Zarpar',
				publisher: { '@id': `${ZARPAR_ORIGIN}/#organization` }
			},
			{
				'@type': 'WebPage',
				'@id': `${input.pageUrl}#webpage`,
				url: input.pageUrl,
				name: input.title,
				description: input.description,
				isPartOf: { '@id': `${ZARPAR_ORIGIN}/#website` },
				about: { '@id': `${ZARPAR_ORIGIN}/#organization` }
			}
		]
	};
}
